import { useEffect, useState } from "react";

import type { Shape, TreeNode } from "../types";
import { Markdown, MetaLinks } from "./Markdown";
import { NodeComments } from "./NodeComments";

interface MetaLinkInput {
  url: unknown;
  label?: unknown;
}

function extractMetaLinks(meta: Record<string, unknown>): { url: string; label?: string }[] {
  const raw = meta["links"];
  if (!Array.isArray(raw)) return [];
  return raw
    .filter((l): l is MetaLinkInput => typeof l === "object" && l !== null && "url" in l)
    .map((l) => ({
      url: typeof l.url === "string" ? l.url : "",
      label: typeof l.label === "string" ? l.label : undefined,
    }))
    .filter((l) => l.url.length > 0);
}

interface Props {
  node: TreeNode;
  onPatch: (patch: { title?: string; note?: string; color?: string; shape?: Shape; font_size?: number; checked?: boolean }) => void;
  onDelete: () => void;
  onAddChild: () => void;
  onMoveSibling: (direction: "up" | "down") => void;
  readOnly?: boolean;
}

const SHAPES: Shape[] = ["rect", "rounded", "ellipse", "diamond", "hex"];
const PRESET_COLORS = ["#ffffff", "#fef3c7", "#dcfce7", "#dbeafe", "#fae8ff", "#fee2e2", "#e5e7eb"];

export function NodePanel({ node, onPatch, onDelete, onAddChild, onMoveSibling, readOnly = false }: Props) {
  const [title, setTitle] = useState(node.title);
  const [note, setNote] = useState(node.note);
  const [notePreview, setNotePreview] = useState(false);
  const [commentCount, setCommentCount] = useState(node.comment_count);

  useEffect(() => {
    setTitle(node.title);
    setNote(node.note);
    setCommentCount(node.comment_count);
  }, [node.id, node.title, node.note, node.comment_count]);

  const metaLinks = extractMetaLinks(node.meta);

  const isLeaf = node.children.length === 0;
  const isRoot = node.parent_id === null;

  return (
    <div style={{ padding: 16, display: "flex", flexDirection: "column", gap: 12 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <h3 style={{ margin: 0, fontSize: 14, textTransform: "uppercase", color: "#475569" }}>Node</h3>
        <span style={{ fontSize: 11, color: "#94a3b8" }}>
          {commentCount > 0 && <span style={{ marginRight: 8 }}>💬 {commentCount}</span>}
          v{node.version}
        </span>
      </div>

      <label style={{ fontSize: 12, color: "#475569" }}>
        Title
        <input
          value={title}
          readOnly={readOnly}
          onChange={(e) => setTitle(e.target.value)}
          onBlur={() => {
            if (!readOnly && title !== node.title) onPatch({ title });
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") (e.target as HTMLInputElement).blur();
          }}
          style={{
            width: "100%",
            padding: "6px 8px",
            border: "1px solid #cbd5e1",
            borderRadius: 4,
            fontSize: 14,
            marginTop: 4,
            background: readOnly ? "#f1f5f9" : "#fff",
          }}
        />
      </label>

      <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <label htmlFor="node-note" style={{ fontSize: 12, color: "#475569" }}>
            Note <span style={{ color: "#94a3b8", fontWeight: 400 }}>(Markdown, ≤ 8000 · {note.length})</span>
          </label>
          {note.length > 0 && (
            <button
              type="button"
              onClick={() => setNotePreview((v) => !v)}
              style={{
                fontSize: 11,
                padding: "2px 8px",
                background: notePreview ? "#2563eb" : "#fff",
                color: notePreview ? "#fff" : "#475569",
                border: "1px solid #cbd5e1",
                borderRadius: 3,
                cursor: "pointer",
              }}
              title="Toggle Markdown preview"
            >
              {notePreview ? "edit" : "preview"}
            </button>
          )}
        </div>
        {notePreview ? (
          <div
            style={{
              border: "1px solid #cbd5e1",
              borderRadius: 4,
              padding: "6px 10px",
              fontSize: 13,
              minHeight: 120,
              background: "#f8fafc",
            }}
          >
            <Markdown source={note} />
          </div>
        ) : (
          <textarea
            id="node-note"
            value={note}
            readOnly={readOnly}
            onChange={(e) => setNote(e.target.value)}
            onBlur={() => {
              if (!readOnly && note !== node.note) onPatch({ note });
            }}
            rows={8}
            maxLength={8000}
            placeholder="ad-hoc context for this node — agent instructions, links, references, longer description. Supports markdown: [label](https://...)"
            style={{
              width: "100%",
              padding: "6px 8px",
              border: "1px solid #cbd5e1",
              borderRadius: 4,
              fontSize: 13,
              resize: "vertical",
              fontFamily: "inherit",
              background: readOnly ? "#f1f5f9" : "#fff",
            }}
          />
        )}
        {metaLinks.length > 0 && <MetaLinks links={metaLinks} />}
      </div>

      <div>
        <div style={{ fontSize: 12, color: "#475569", marginBottom: 4 }}>Color</div>
        <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
          {PRESET_COLORS.map((c) => (
            <button
              key={c}
              disabled={readOnly}
              onClick={() => onPatch({ color: c })}
              style={{
                width: 22,
                height: 22,
                borderRadius: 4,
                background: c,
                border: node.color === c ? "2px solid #2563eb" : "1px solid #94a3b8",
                cursor: readOnly ? "not-allowed" : "pointer",
                opacity: readOnly ? 0.5 : 1,
              }}
              title={c}
            />
          ))}
          <input
            type="color"
            value={node.color.startsWith("#") ? node.color : "#ffffff"}
            disabled={readOnly}
            onChange={(e) => onPatch({ color: e.target.value })}
            style={{ width: 28, height: 24, border: "none", padding: 0, marginLeft: 4, opacity: readOnly ? 0.5 : 1 }}
          />
        </div>
      </div>

      <div>
        <div style={{ fontSize: 12, color: "#475569", marginBottom: 4 }}>Shape</div>
        <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
          {SHAPES.map((s) => (
            <button
              key={s}
              disabled={readOnly}
              onClick={() => onPatch({ shape: s })}
              style={{
                padding: "4px 10px",
                background: node.shape === s ? "#2563eb" : "#fff",
                color: node.shape === s ? "#fff" : "#111",
                border: "1px solid #cbd5e1",
                borderRadius: 4,
                fontSize: 12,
                cursor: readOnly ? "not-allowed" : "pointer",
                opacity: readOnly ? 0.5 : 1,
              }}
            >
              {s}
            </button>
          ))}
        </div>
      </div>

      <label style={{ fontSize: 12, color: "#475569" }}>
        Font size: {node.font_size}px
        <input
          type="range"
          min={10}
          max={32}
          value={node.font_size}
          disabled={readOnly}
          onChange={(e) => onPatch({ font_size: parseInt(e.target.value, 10) })}
          style={{ width: "100%", opacity: readOnly ? 0.5 : 1 }}
        />
      </label>

      {isLeaf && (
        <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 14, opacity: readOnly ? 0.6 : 1 }}>
          <input
            type="checkbox"
            checked={node.checked}
            disabled={readOnly}
            onChange={(e) => onPatch({ checked: e.target.checked })}
          />
          Checked
        </label>
      )}
      {!isLeaf && (
        <div style={{ fontSize: 12, color: "#64748b", padding: "6px 8px", background: "#f1f5f9", borderRadius: 4 }}>
          Aggregate: <strong>{node.agg_state}</strong> ({node.children.length} children)
        </div>
      )}

      {!readOnly && (
        <div style={{ borderTop: "1px solid #e2e8f0", paddingTop: 12, display: "flex", flexDirection: "column", gap: 6 }}>
          <button
            onClick={onAddChild}
            style={{ padding: "8px", background: "#2563eb", color: "#fff", border: "none", borderRadius: 4, cursor: "pointer" }}
          >
            + Add child
          </button>
          {!isRoot && (
            <>
              <div style={{ display: "flex", gap: 4 }}>
                <button
                  onClick={() => onMoveSibling("up")}
                  style={{ flex: 1, padding: "6px", background: "#fff", border: "1px solid #cbd5e1", borderRadius: 4, cursor: "pointer" }}
                >
                  ↑ Move up
                </button>
                <button
                  onClick={() => onMoveSibling("down")}
                  style={{ flex: 1, padding: "6px", background: "#fff", border: "1px solid #cbd5e1", borderRadius: 4, cursor: "pointer" }}
                >
                  ↓ Move down
                </button>
              </div>
              <button
                onClick={() => {
                  if (confirm(`Delete "${node.title}" and all its descendants?`)) onDelete();
                }}
                style={{ padding: "6px", background: "#fff", color: "#dc2626", border: "1px solid #dc2626", borderRadius: 4, cursor: "pointer" }}
              >
                Delete
              </button>
            </>
          )}
        </div>
      )}

      <NodeComments
        nodeId={node.id}
        initialCount={node.comment_count}
        onCountChange={setCommentCount}
      />
    </div>
  );
}
